import { VFXEffectConfig } from '../../VFXEffectRegistry';

// =============================================================================
// PROCEDURAL COMPOSITE PRESETS
// Built from existing primitive types with specialized tween/scatter/orbit configs
// =============================================================================

/**
 * Vortex — 8 ellipses with staggered orbit speeds, spiraling inward.
 * Use for suction / void abilities.
 */
const VORTEX: VFXEffectConfig = {
  id: 'procedural_vortex',
  name: 'Vortex',
  category: 'burst',
  description: 'Spiraling inward vortex effect',
  duration: 1200,
  primitives: Array.from({ length: 8 }, (_, i) => ({
    type: 'ellipse' as const,
    width: 14 - i,
    height: 6,
    color: 0x8800FF,
    alpha: 0.6 - i * 0.05,
    depth: 93 + i,
    orbit: {
      radius: 50 - i * 4,
      speed: 1.5 + i * 0.3,
      startAngle: (i / 8) * Math.PI * 2,
      orientToPath: true,
    },
    tween: {
      alpha: 0,
      scale: 0.3,
      duration: 1000 + i * 50,
      ease: 'Power2',
    },
  })),
};

/**
 * Lightning chain — scattered star sparks along a horizontal line
 * with flicker tweens. Use for chain lightning / electric effects.
 */
const LIGHTNING_CHAIN: VFXEffectConfig = {
  id: 'procedural_lightning_chain',
  name: 'Lightning Chain',
  category: 'burst',
  description: 'Sparking chain lightning between points',
  primitives: [
    // Main beam (using the new beam primitive)
    {
      type: 'beam',
      beamLength: 120,
      beamWidth: 3,
      beamSegments: 6,
      lineJitter: 8,
      color: 0xFFDD44,
      alpha: 0.9,
      depth: 95,
      tween: {
        alpha: 0,
        duration: 200,
        ease: 'Power1',
      },
    },
    // Spark stars along the path
    {
      type: 'star',
      points: 4,
      innerRadius: 2,
      outerRadius: 5,
      color: 0xFFFFFF,
      alpha: 0.9,
      depth: 96,
      tween: {
        alpha: 0,
        rotation: Math.PI * 0.5,
        scale: 0.5,
        duration: 150,
        ease: 'Power2',
      },
      scatter: { count: 5, radius: 50 },
    },
    // Glow halo at origin
    {
      type: 'circle',
      radius: 12,
      color: 0x4488FF,
      alpha: 0.4,
      depth: 94,
      tween: {
        alpha: 0,
        scale: 1.8,
        duration: 250,
        ease: 'Power2',
      },
    },
  ],
};

/**
 * Trail fade — circles at decreasing alpha with staggered delays.
 * Use for movement trails, afterimages.
 */
const TRAIL_FADE: VFXEffectConfig = {
  id: 'procedural_trail_fade',
  name: 'Trail Fade',
  category: 'burst',
  description: 'Fading trail of circular afterimages',
  primitives: Array.from({ length: 5 }, (_, i) => ({
    type: 'circle' as const,
    radius: 10 - i,
    color: 0xFFFFFF,
    alpha: 0.5 - i * 0.08,
    depth: 90,
    offsetX: -i * 12,
    tween: {
      alpha: 0,
      scale: 0.6,
      duration: 300,
      ease: 'Power2',
      delay: i * 40,
    },
  })),
};

/**
 * Ring expand — circles in a ring scatter outward in sync.
 * Use for shockwave / AoE impact rings.
 */
const RING_EXPAND: VFXEffectConfig = {
  id: 'procedural_ring_expand',
  name: 'Ring Expand',
  category: 'burst',
  description: 'Expanding ring of particles',
  primitives: [
    // Ring particles expanding outward
    {
      type: 'circle',
      radius: 5,
      color: 0xFFDD44,
      alpha: 0.8,
      depth: 94,
      tween: {
        alpha: 0,
        scale: 0.4,
        duration: 500,
        ease: 'Power2',
      },
      scatter: { count: 12, radius: 60 },
    },
    // Central flash
    {
      type: 'circle',
      radius: 15,
      color: 0xFFFFFF,
      alpha: 0.6,
      depth: 95,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 300,
        ease: 'Power1',
      },
    },
    // Outer ring stroke (using shockwave primitive)
    {
      type: 'shockwave',
      shockwaveInnerRadius: 25,
      shockwaveOuterRadius: 35,
      color: 0xFFDD44,
      alpha: 0.5,
      depth: 93,
      tween: {
        alpha: 0,
        scale: 2.5,
        duration: 600,
        ease: 'Power2',
      },
    },
  ],
};

// =============================================================================
// EXPORTS
// =============================================================================

export const PROCEDURAL_VFX: VFXEffectConfig[] = [
  VORTEX,
  LIGHTNING_CHAIN,
  TRAIL_FADE,
  RING_EXPAND,
];
